import React, { useMemo, useState } from "react";
import { HiX, HiOutlineSearch } from "react-icons/hi";

// Shared checkbox-list control for picking one or more depots (zones).
// Used by both the "new operator" and "edit operator" forms.
function ZonesMultiSelect({ depots, selectedIds, onChange }) {
  const [search, setSearch] = useState("");

  const normalizedSearch = search.trim().toLowerCase();

  const filteredDepots = useMemo(() => {
    if (!normalizedSearch) return depots;
    return depots.filter((depot) => {
      const name = (depot?.Name || "").toLowerCase();
      const origin = (depot?.Origin || "").toLowerCase();
      return name.includes(normalizedSearch) || origin.includes(normalizedSearch);
    });
  }, [depots, normalizedSearch]);

  const selectedDepots = useMemo(
    () => depots.filter((depot) => selectedIds.includes(depot._id)),
    [depots, selectedIds]
  );

  const allFilteredSelected =
    filteredDepots.length > 0 &&
    filteredDepots.every((depot) => selectedIds.includes(depot._id));

  const toggleDepot = (depotId) => {
    onChange(
      selectedIds.includes(depotId)
        ? selectedIds.filter((id) => id !== depotId)
        : [...selectedIds, depotId]
    );
  };

  const removeDepot = (depotId) => {
    onChange(selectedIds.filter((id) => id !== depotId));
  };

  const selectAllFiltered = () => {
    const filteredIds = filteredDepots.map((depot) => depot._id);
    onChange(Array.from(new Set([...selectedIds, ...filteredIds])));
  };

  const clearAll = () => onChange([]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-md font-bold text-navy-700">Zones (dépôts)</label>
        <span className="text-xs text-gray-400">
          {selectedIds.length > 0
            ? `${selectedIds.length} sélectionnée${selectedIds.length > 1 ? "s" : ""}`
            : "Aucune zone sélectionnée"}
        </span>
      </div>

      {selectedDepots.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selectedDepots.map((depot) => (
            <span
              key={depot._id}
              className="flex items-center gap-1 rounded-full bg-[#236571]/10 py-1 pl-2.5 pr-1 text-xs font-medium text-[#236571]"
            >
              {depot.Name}
              <button
                type="button"
                onClick={() => removeDepot(depot._id)}
                aria-label={`Retirer ${depot.Name}`}
                className="rounded-full p-0.5 text-[#236571] hover:bg-[#236571]/20"
              >
                <HiX size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="mt-2 overflow-hidden rounded-xl border border-gray-200">
        <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
          <HiOutlineSearch className="text-gray-400" size={16} />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher une zone..."
            className="w-full bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
          />
          <button
            type="button"
            onClick={allFilteredSelected ? clearAll : selectAllFiltered}
            className="shrink-0 whitespace-nowrap text-xs font-semibold text-[#236571] hover:underline"
          >
            {allFilteredSelected ? "Tout désélectionner" : "Tout sélectionner"}
          </button>
        </div>

        <div className="max-h-40 overflow-y-auto p-1.5">
          {filteredDepots.length === 0 && (
            <p className="px-2 py-3 text-center text-sm text-gray-400">
              Aucune zone trouvée
            </p>
          )}
          {filteredDepots.map((depot) => {
            const isChecked = selectedIds.includes(depot._id);
            return (
              <label
                key={depot._id}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border-l-4 px-2 py-1.5 text-sm transition-colors ${
                  isChecked
                    ? "border-[#236571] bg-[#236571]/5 text-navy-700"
                    : "border-transparent text-gray-600 hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleDepot(depot._id)}
                  className="rounded border-gray-300 text-[#236571] focus:ring-[#236571]"
                />
                <span className="truncate">{depot.Name}</span>
                {depot.Origin && (
                  <span className="ml-auto shrink-0 text-xs text-gray-400">
                    {depot.Origin}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ZonesMultiSelect;
